import React, { useState, useCallback, useEffect, useRef } from 'react';
import { MapPin, Search, CheckCircle, X, Navigation, Globe } from 'lucide-react';
import { t } from '../i18n';
import { useLanguage } from '../contexts/LanguageContext';

// Country configurations with their states/provinces
const COUNTRY_CONFIG = {
  'US': {
    name: 'United States',
    stateField: 'State',
    postalCodeFormat: '12345',
    postalCodePattern: /^\d{5}(-\d{4})?$/,
    states: [
      'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
      'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
      'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
      'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
      'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
      'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
      'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming', 'District of Columbia'
    ]
  },
  'CA': {
    name: 'Canada',
    stateField: 'Province',
    postalCodeFormat: 'A1A 1A1',
    postalCodePattern: /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/,
    states: [
      'Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador',
      'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island',
      'Quebec', 'Saskatchewan', 'Yukon'
    ]
  },
  'TR': {
    name: 'Türkiye',
    stateField: 'Şehir',
    postalCodeFormat: '12345',
    postalCodePattern: /^\d{5}$/,
    states: [
      'Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Amasya', 'Ankara', 'Antalya', 'Artvin',
      'Aydın', 'Balıkesir', 'Bilecik', 'Bingöl', 'Bitlis', 'Bolu', 'Burdur', 'Bursa',
      'Çanakkale', 'Çankırı', 'Çorum', 'Denizli', 'Diyarbakır', 'Edirne', 'Elazığ', 'Erzincan',
      'Erzurum', 'Eskişehir', 'Gaziantep', 'Giresun', 'Gümüşhane', 'Hakkâri', 'Hatay', 'Isparta',
      'Mersin', 'İstanbul', 'İzmir', 'Kars', 'Kastamonu', 'Kayseri', 'Kırklareli', 'Kırşehir',
      'Kocaeli', 'Konya', 'Kütahya', 'Malatya', 'Manisa', 'Kahramanmaraş', 'Mardin', 'Muğla',
      'Muş', 'Nevşehir', 'Niğde', 'Ordu', 'Rize', 'Sakarya', 'Samsun', 'Siirt', 'Sinop',
      'Sivas', 'Tekirdağ', 'Tokat', 'Trabzon', 'Tunceli', 'Şanlıurfa', 'Uşak', 'Van',
      'Yozgat', 'Zonguldak', 'Aksaray', 'Bayburt', 'Karaman', 'Kırıkkale', 'Batman', 'Şırnak',
      'Bartın', 'Ardahan', 'Iğdır', 'Yalova', 'Karabük', 'Kilis', 'Osmaniye', 'Düzce'
    ]
  },
  'DE': {
    name: 'Germany',
    stateField: 'State',
    postalCodeFormat: '12345',
    postalCodePattern: /^\d{5}$/,
    states: [
      'Baden-Württemberg', 'Bayern', 'Berlin', 'Brandenburg', 'Bremen', 'Hamburg',
      'Hessen', 'Mecklenburg-Vorpommern', 'Niedersachsen', 'Nordrhein-Westfalen',
      'Rheinland-Pfalz', 'Saarland', 'Sachsen', 'Sachsen-Anhalt', 'Schleswig-Holstein', 'Thüringen'
    ]
  },
  'GB': {
    name: 'United Kingdom',
    stateField: 'County',
    postalCodeFormat: 'SW1A 1AA',
    postalCodePattern: /^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/i,
    states: [
      'England', 'Scotland', 'Wales', 'Northern Ireland'
    ]
  },
  'FR': {
    name: 'France',
    stateField: 'Department',
    postalCodeFormat: '12345',
    postalCodePattern: /^\d{5}$/,
    states: [
      'Ain', 'Aisne', 'Allier', 'Alpes-de-Haute-Provence', 'Hautes-Alpes', 'Alpes-Maritimes',
      'Ardèche', 'Ardennes', 'Ariège', 'Aube', 'Aude', 'Aveyron', 'Bouches-du-Rhône', 'Calvados',
      'Cantal', 'Charente', 'Charente-Maritime', 'Cher', 'Corrèze', 'Corse-du-Sud', 'Haute-Corse',
      'Côte-d\'Or', 'Côtes-d\'Armor', 'Creuse', 'Dordogne', 'Doubs', 'Drôme', 'Eure', 'Eure-et-Loir',
      'Finistère', 'Gard', 'Haute-Garonne', 'Gers', 'Gironde', 'Hérault', 'Ille-et-Vilaine',
      'Indre', 'Indre-et-Loire', 'Isère', 'Jura', 'Landes', 'Loir-et-Cher', 'Loire', 'Haute-Loire',
      'Loire-Atlantique', 'Loiret', 'Lot', 'Lot-et-Garonne', 'Lozère', 'Maine-et-Loire', 'Manche',
      'Marne', 'Haute-Marne', 'Mayenne', 'Meurthe-et-Moselle', 'Meuse', 'Morbihan', 'Moselle',
      'Nièvre', 'Nord', 'Oise', 'Orne', 'Pas-de-Calais', 'Puy-de-Dôme', 'Pyrénées-Atlantiques',
      'Hautes-Pyrénées', 'Pyrénées-Orientales', 'Bas-Rhin', 'Haut-Rhin', 'Rhône', 'Haute-Saône',
      'Saône-et-Loire', 'Sarthe', 'Savoie', 'Haute-Savoie', 'Paris', 'Seine-Maritime', 'Seine-et-Marne',
      'Yvelines', 'Deux-Sèvres', 'Somme', 'Tarn', 'Tarn-et-Garonne', 'Var', 'Vaucluse', 'Vendée',
      'Vienne', 'Haute-Vienne', 'Vosges', 'Yonne', 'Territoire de Belfort', 'Essonne', 'Hauts-de-Seine',
      'Seine-Saint-Denis', 'Val-de-Marne', 'Val-d\'Oise'
    ]
  }
};

const COUNTRIES = [
  { code: 'TR', name: 'Türkiye' },
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'DE', name: 'Germany' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'FR', name: 'France' },
  { code: 'OTHER', name: 'Other' }
];

/**
 * International Address Input Component
 * Supports different countries with their specific address formats
 */
const InternationalAddressInput = React.memo(({ 
  value = {}, 
  onChange, 
  isDarkMode = false, 
  error = {},
  label = "Address Information"
}) => {
  const { currentLanguage } = useLanguage();
  
  const [address, setAddress] = useState({
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'TR',
    ...value
  });
  
  const [showStateSuggestions, setShowStateSuggestions] = useState(false);
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [isLocating, setIsLocating] = useState(false);
  const [locationSupported, setLocationSupported] = useState(false);
  
  const stateInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  
  // Check geolocation support
  useEffect(() => {
    setLocationSupported('geolocation' in navigator);
  }, []);
  
  // Get current country configuration
  const currentCountryConfig = COUNTRY_CONFIG[address.country] || COUNTRY_CONFIG['OTHER'];
  
  // Filter states/provinces based on search term
  const filteredStates = currentCountryConfig.states 
    ? stateSearchTerm.length > 0 
      ? currentCountryConfig.states.filter(state => 
          state.toLowerCase().includes(stateSearchTerm.toLowerCase())
        ).slice(0, 8)
      : []
    : [];
  
  // Address change handler
  const handleAddressChange = useCallback((field, newValue) => {
    const updatedAddress = { ...address, [field]: newValue };
    
    // Reset state when country changes
    if (field === 'country') {
      updatedAddress.state = '';
      setStateSearchTerm('');
    }
    
    setAddress(updatedAddress);
    onChange && onChange(updatedAddress);
  }, [address, onChange]);
  
  // State selection
  const handleStateSelect = useCallback((selectedState) => {
    handleAddressChange('state', selectedState);
    setStateSearchTerm('');
    setShowStateSuggestions(false);
  }, [handleAddressChange]);
    // Enhanced location with improved accuracy and reverse geocoding
  const getCurrentLocation = useCallback(async () => {
    if (!locationSupported) return;
    
    setIsLocating(true);
    
    try {
      // Enhanced geolocation options for better accuracy
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 30000, // Increased timeout for better accuracy
          maximumAge: 300000 // 5 minutes cache
        });
      });
      
      const { latitude, longitude, accuracy } = position.coords;
      
      // Enhanced reverse geocoding with multiple attempts for accuracy
      let locationData = null;
      try {
        // Try multiple reverse geocoding services for better results
        const reverseGeocodingAttempts = [
          // Primary: Nominatim (OpenStreetMap)
          async () => {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1&accept-language=${currentLanguage || 'en'}`
            );
            const data = await response.json();
            return data;
          },
          // Fallback: Alternative geocoding service could be added here
        ];
        
        for (const attempt of reverseGeocodingAttempts) {
          try {
            locationData = await attempt();
            if (locationData && locationData.address) break;
          } catch (err) {
            console.warn('Reverse geocoding attempt failed:', err);
          }
        }
      } catch (geoError) {
        console.warn('Reverse geocoding failed:', geoError);
      }
      
      let updatedAddress = { ...address };
      
      // Enhanced address parsing for 2031 DAPPS structure
      if (locationData && locationData.address) {
        const addr = locationData.address;
        
        // Determine country code
        const countryCode = addr.country_code?.toUpperCase() || 'TR';
        const countryName = COUNTRY_CONFIG[countryCode]?.name || addr.country || 'Unknown';
        
        updatedAddress = {
          ...address,
          coordinates: { 
            latitude, 
            longitude, 
            accuracy: Math.round(accuracy),
            timestamp: new Date().toISOString()
          },
          street: addr.house_number && addr.road 
            ? `${addr.road} ${addr.house_number}` 
            : addr.road || `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
          city: addr.city || addr.town || addr.village || addr.municipality || '',
          state: addr.state || addr.province || addr.region || '',
          postalCode: addr.postcode || '',
          country: countryCode,
          district: addr.suburb || addr.neighbourhood || addr.quarter || '',
          // Additional 2031 DAPPS metadata
          locationMetadata: {
            confidence: locationData.importance || 0.5,
            displayName: locationData.display_name,
            boundingBox: locationData.boundingbox,
            osmId: locationData.osm_id,
            osmType: locationData.osm_type,
            placeRank: locationData.place_rank
          }
        };
      } else {
        // Fallback when reverse geocoding fails
        updatedAddress = {
          ...address,
          coordinates: { 
            latitude, 
            longitude, 
            accuracy: Math.round(accuracy),
            timestamp: new Date().toISOString()
          },
          street: `Koordinat: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
        };
      }
      
      setAddress(updatedAddress);
      onChange && onChange(updatedAddress);
      
    } catch (error) {
      console.error('Location error:', error);
      let errorMessage = 'Konum alınamadı';
      
      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = 'Konum izni reddedildi';
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = 'Konum bilgisi mevcut değil';
          break;
        case error.TIMEOUT:
          errorMessage = 'Konum alma zaman aşımı';
          break;
        default:
          errorMessage = `Konum hatası: ${error.message}`;
      }
      
      // Show error to user (could be enhanced with toast notification)
      console.warn(errorMessage);
    } finally {
      setIsLocating(false);
    }
  }, [locationSupported, address, onChange, currentLanguage]);
  
  // State input change handler
  const handleStateInputChange = useCallback((e) => {
    const value = e.target.value;
    setStateSearchTerm(value);
    handleAddressChange('state', value);
    setShowStateSuggestions(value.length > 0 && currentCountryConfig.states);
  }, [handleAddressChange, currentCountryConfig.states]);
  
  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowStateSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Validate postal code based on country
  const validatePostalCode = (code) => {
    if (!code || !currentCountryConfig.postalCodePattern) return true;
    return currentCountryConfig.postalCodePattern.test(code);
  };
  
  return (
    <div className="space-y-4">
      {/* Header with Location Button */}
      <div className="flex justify-between items-center">
        <h3 className={`text-lg font-semibold flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          <MapPin size={20} className="mr-2" />
          {label}
        </h3>
        
        {locationSupported && (
          <button
            onClick={getCurrentLocation}
            disabled={isLocating}
            className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 ${
              isDarkMode 
                ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            } ${isLocating ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Navigation size={16} className={isLocating ? 'animate-spin' : ''} />
            <span className="text-sm font-medium">
              {isLocating ? t('address.locating', 'Getting Location...') : t('address.getLocation', 'Get My Location')}
            </span>
          </button>
        )}
      </div>
      
      {/* Address Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Country Selection */}
        <div>
          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {t('address.country', 'Country')} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <select
              value={address.country || 'TR'}
              onChange={(e) => handleAddressChange('country', e.target.value)}
              className={`w-full p-3 rounded-xl transition-all duration-200 border-2 border-transparent focus:border-blue-500 appearance-none ${
                isDarkMode 
                  ? 'bg-gray-700 text-white' 
                  : 'bg-gray-100 text-gray-900'
              } focus:outline-none focus:ring-4 focus:ring-blue-500/20`}
            >
              {COUNTRIES.map(country => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
            <Globe size={16} className={`absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`} />
          </div>
        </div>
        
        {/* Street Address */}
        <div className="md:col-span-1">
          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {t('address.street', 'Street Address')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={address.street || ''}
            onChange={(e) => handleAddressChange('street', e.target.value)}
            placeholder={address.country === 'US' ? "123 Main Street" : address.country === 'TR' ? "Atatürk Caddesi No: 123/A" : "Street Address"}
            className={`w-full p-3 rounded-xl transition-all duration-200 border-2 ${
              error.street 
                ? 'border-red-500 focus:border-red-500' 
                : 'border-transparent focus:border-blue-500'
            } ${
              isDarkMode 
                ? 'bg-gray-700 text-white placeholder-gray-400' 
                : 'bg-gray-100 text-gray-900 placeholder-gray-500'
            } focus:outline-none focus:ring-4 focus:ring-blue-500/20`}
          />
          {error.street && (
            <p className="text-red-500 text-xs mt-1 flex items-center">
              <X size={12} className="mr-1" />
              {error.street}
            </p>
          )}
        </div>
        
        {/* City */}
        <div>
          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {t('address.city', 'City')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={address.city || ''}
            onChange={(e) => handleAddressChange('city', e.target.value)}
            placeholder={address.country === 'US' ? "New York" : address.country === 'TR' ? "İstanbul" : "City"}
            className={`w-full p-3 rounded-xl transition-all duration-200 border-2 ${
              error.city 
                ? 'border-red-500 focus:border-red-500' 
                : 'border-transparent focus:border-blue-500'
            } ${
              isDarkMode 
                ? 'bg-gray-700 text-white placeholder-gray-400' 
                : 'bg-gray-100 text-gray-900 placeholder-gray-500'
            } focus:outline-none focus:ring-4 focus:ring-blue-500/20`}
          />
          {error.city && (
            <p className="text-red-500 text-xs mt-1 flex items-center">
              <X size={12} className="mr-1" />
              {error.city}
            </p>
          )}
        </div>
        
        {/* State/Province (with autocomplete for supported countries) */}
        <div className="relative" ref={suggestionsRef}>
          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {currentCountryConfig.stateField} {currentCountryConfig.states ? <span className="text-red-500">*</span> : ''}
          </label>
          <div className="relative">
            <input
              ref={stateInputRef}
              type="text"
              value={address.state || ''}
              onChange={handleStateInputChange}
              placeholder={
                address.country === 'US' ? "California" : 
                address.country === 'TR' ? "İstanbul" : 
                address.country === 'CA' ? "Ontario" :
                currentCountryConfig.stateField
              }
              className={`w-full p-3 rounded-xl transition-all duration-200 border-2 ${
                error.state 
                  ? 'border-red-500 focus:border-red-500' 
                  : 'border-transparent focus:border-blue-500'
              } ${
                isDarkMode 
                  ? 'bg-gray-700 text-white placeholder-gray-400' 
                  : 'bg-gray-100 text-gray-900 placeholder-gray-500'
              } focus:outline-none focus:ring-4 focus:ring-blue-500/20`}
            />
            {currentCountryConfig.states && (
              <Search size={16} className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`} />
            )}
          </div>
          
          {/* State Suggestions */}
          {showStateSuggestions && filteredStates.length > 0 && (
            <div className={`absolute z-10 w-full mt-1 rounded-xl shadow-lg border max-h-48 overflow-y-auto ${
              isDarkMode 
                ? 'bg-gray-800 border-gray-600' 
                : 'bg-white border-gray-200'
            }`}>
              {filteredStates.map((state, index) => (
                <button
                  key={state}
                  onClick={() => handleStateSelect(state)}
                  className={`w-full text-left px-4 py-3 hover:bg-blue-500 hover:text-white transition-colors ${
                    index === 0 ? 'rounded-t-xl' : ''
                  } ${
                    index === filteredStates.length - 1 ? 'rounded-b-xl' : ''
                  } ${
                    isDarkMode ? 'text-gray-300 hover:bg-blue-600' : 'text-gray-700'
                  }`}
                >
                  {state}
                </button>
              ))}
            </div>
          )}
          
          {error.state && (
            <p className="text-red-500 text-xs mt-1 flex items-center">
              <X size={12} className="mr-1" />
              {error.state}
            </p>
          )}
        </div>
        
        {/* Postal Code */}
        <div>
          <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {address.country === 'US' ? 'ZIP Code' : address.country === 'GB' ? 'Postcode' : t('address.postalCode', 'Postal Code')}
          </label>
          <input
            type="text"
            value={address.postalCode || ''}
            onChange={(e) => {
              const value = e.target.value;
              // Apply country-specific formatting
              if (address.country === 'US') {
                const formatted = value.replace(/\D/g, '').slice(0, 5);
                handleAddressChange('postalCode', formatted);
              } else if (address.country === 'TR') {
                const formatted = value.replace(/\D/g, '').slice(0, 5);
                handleAddressChange('postalCode', formatted);
              } else if (address.country === 'CA') {
                const formatted = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6);
                const final = formatted.length > 3 ? `${formatted.slice(0, 3)} ${formatted.slice(3)}` : formatted;
                handleAddressChange('postalCode', final);
              } else if (address.country === 'GB') {
                handleAddressChange('postalCode', value.toUpperCase().slice(0, 8));
              } else {
                handleAddressChange('postalCode', value);
              }
            }}
            placeholder={currentCountryConfig.postalCodeFormat}
            className={`w-full p-3 rounded-xl transition-all duration-200 border-2 ${
              error.postalCode || (address.postalCode && !validatePostalCode(address.postalCode))
                ? 'border-red-500 focus:border-red-500' 
                : 'border-transparent focus:border-blue-500'
            } ${
              isDarkMode 
                ? 'bg-gray-700 text-white placeholder-gray-400' 
                : 'bg-gray-100 text-gray-900 placeholder-gray-500'
            } focus:outline-none focus:ring-4 focus:ring-blue-500/20`}
          />
          {(error.postalCode || (address.postalCode && !validatePostalCode(address.postalCode))) && (
            <p className="text-red-500 text-xs mt-1 flex items-center">
              <X size={12} className="mr-1" />
              {error.postalCode || t('address.invalidPostalCode', 'Invalid postal code format')}
            </p>
          )}
        </div>
      </div>
      
      {/* Location Coordinates (if available) */}
      {address.coordinates && (
        <div className={`p-3 rounded-lg border ${
          isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-gray-50 border-gray-200'
        }`}>
          <div className="flex items-center text-sm">
            <CheckCircle size={16} className="text-green-500 mr-2" />
            <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
              {t('address.locationSet', 'Location determined')}: {address.coordinates.latitude.toFixed(6)}, {address.coordinates.longitude.toFixed(6)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
});

InternationalAddressInput.displayName = 'InternationalAddressInput';

export default InternationalAddressInput;
